<template>
  <div
    class="flex border-b last:border-b-0 border-neutral-200 transition duration-200 ease-in-out"
    :class="bulkSelected ? 'bg-project-50' : 'bg-white hover:bg-neutral-50'"
  >
    <div
      class="flex items-center justify-center flex-shrink-0 overflow-hidden transition-all duration-200 ease-in-out"
      :class="bulkSelectable ? 'w-12 opacity-100 border-r border-neutral-200' : 'w-0 opacity-0 pointer-events-none'"
      @click.stop
    >
      <input
        type="checkbox"
        class="w-4 h-4 cursor-pointer"
        style="accent-color: var(--accent-color)"
        :checked="bulkSelected"
        @click="$emit('toggle-bulk', post.id, $event.shiftKey)"
      />
    </div>
    <a
      @click.prevent="castVote()"
      href="#"
      class="text-neutral-600 text-lg flex-shrink-0 border-r hover:no-underline transition duration-200 ease-in-out w-16 flex items-center justify-center py-4"
      :class="{
        'text-project-500': alreadyVoted,
        'vote-closed': votesDisabled,
      }"
      v-tippy="{ placement: 'top', content: $t('Voting is closed for this idea'), onShow: () => votesDisabled }"
    >
      <div class="flex flex-col items-center">
        <svg
          class="w-3 h-3 rotate-180 transition-transform duration-200 ease-in-out mb-1.5"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 30 30"
          :class="{ '-translate-y-0.5': isAnimating }"
        >
          <path
            d="M15,23c-0.256,0-0.512-0.098-0.707-0.293l-10-10c-0.286-0.286-0.372-0.716-0.217-1.09C4.23,11.243,4.596,11,5,11h20 c0.404,0,0.77,0.243,0.924,0.617c0.155,0.374,0.069,0.804-0.217,1.09l-10,10C15.512,22.902,15.256,23,15,23z"
          ></path>
        </svg>
        <div
          id="post-upvotes"
          class="font-bold leading-none text-base"
          v-if="!allowVoteCount || currentProjectAdmin"
        >
          {{ upvotes }}
        </div>
      </div>
    </a>

    <div
      class="flex-1 flex flex-col items-start justify-start p-4 cursor-pointer"
      id="board-post-data"
      @click.prevent="handleRowClick($event)"
    >
      <div v-if="post.pinned || post.status.name !== 'Not set'" class="flex items-center flex-wrap gap-2 mb-2">
        <span v-if="post.pinned" class="inline-flex items-center gap-1 text-xs font-medium text-amber-700 rounded-lg px-2 py-1 bg-amber-100">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M3 3C2.44772 3 2 3.44772 2 4C2 4.55228 2.44772 5 3 5H14C14.5523 5 15 4.55228 15 4C15 3.44772 14.5523 3 14 3H3Z M3 7C2.44772 7 2 7.44772 2 8C2 8.55228 2.44772 9 3 9H8C8.55228 9 9 8.55228 9 8C9 7.44772 8.55228 7 8 7H3Z M3 11C2.44772 11 2 11.4477 2 12C2 12.5523 2.44772 13 3 13H7C7.55228 13 8 12.5523 8 12C8 11.4477 7.55228 11 7 11H3Z M13 16C13 16.5523 13.4477 17 14 17C14.5523 17 15 16.5523 15 16L15 10.4142L16.2929 11.7071C16.6834 12.0976 17.3166 12.0976 17.7071 11.7071C18.0976 11.3166 18.0976 10.6834 17.7071 10.2929L14.7071 7.29289C14.5196 7.10536 14.2652 7 14 7C13.7348 7 13.4804 7.10536 13.2929 7.29289L10.2929 10.2929C9.90237 10.6834 9.90237 11.3166 10.2929 11.7071C10.6834 12.0976 11.3166 12.0976 11.7071 11.7071L13 10.4142L13 16Z" clip-rule="evenodd"></path></svg>
          {{ $t("Pinned") }}
        </span>
        <post-status
          v-if="post.status.name !== 'Not set'"
          :status="post.status.name"
          :color="post.status.color"
          :status-name="statusName"
        ></post-status>
      </div>
      <h3 class="font-semibold" v-line-clamp="lines">
        <a
          @click.prevent.stop="handleRowClick($event)"
          :href="post.url"
          class="text-neutral-900 hover:no-underline transition duration-200 ease-in-out force-break-words"
          id="post-title"
          >{{ post.title }}</a
        >
      </h3>
      <p
        class="text-neutral-700 text-sm mt-1"
        style="word-break: break-word;"
        v-if="post.content"
        v-line-clamp="lines"
      >
        {{ content() }}
      </p>
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 w-full text-neutral-700"
      >
        <div class="flex flex-wrap items-center gap-3">
          <div class="flex items-center gap-1.5 text-xs">
            <template v-if="authorName() !== 'Anonymous'">
              <avatar
                class="avatar border-1 border-white cursor-pointer"
                :size="20"
                :src="authorAvatarUrl()"
                :username="authorName()"
              ></avatar>
              <span>{{ authorName() }}</span>
            </template>
            <template v-else>
              <span>{{ $t('Anonymous') }}</span>
            </template>
          </div>

          <div
            class="flex items-center gap-1.5 text-xs"
            id="board-post-comment"
          >
            <span class="mr-2" id="post-board">
              {{ post.board.name }}
            </span>
            <span class="flex items-center">
              <simple-svg
                :filepath="commentsIcon"
                width="14px"
                height="14px"
                class="mr-1"
              />
              {{ commentsCountHuman() }}
            </span>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <span v-if="postTags && currentProjectAdmin" class="inline-flex items-center text-xs text-neutral-700 bg-neutral-100 rounded-full px-2 py-1">
            <simple-svg
              :filepath="tagsIcon"
              width="14px"
              height="14px"
              class="mx-1"
            />
            {{ postTags }}
          </span>
          <span
            v-if="currentProjectAdmin && post.is_private"
            class="inline-flex items-center gap-1 text-xs text-neutral-700 rounded-full px-2 py-1 bg-neutral-100"
          >
            <simple-svg :filepath="eyeSlashIcon" width="12px" height="12px" />
            {{ $t("Private") }}
          </span>
          <span
            v-if="currentProjectAdmin && !post.is_private && !post.is_spam"
            class="inline-flex items-center gap-1 text-xs text-neutral-700 rounded-full px-2 py-1 bg-neutral-100"
          >
            <simple-svg :filepath="eyeIcon" width="12px" height="12px" />
            {{ $t("Public") }}
          </span>
          <span
            v-if="post.is_spam && currentProjectAdmin"
            class="inline-flex items-center text-xs gap-1 text-red-700 rounded-full px-2 py-1 bg-red-100"
          >
            <simple-svg :filepath="warningIcon" width="12px" height="12px" />
            {{ $t("Marked as spam") }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import upvoteIcon from "images/upvote_chevron.svg";
import tagsIcon from "images/tags.svg";
import commentsIcon from "images/comments.svg";
import eyeIcon from "images/eye.svg";
import eyeSlashIcon from "images/eye-slash.svg";
import warningIcon from "images/warning.svg";
import EventBus from "event_bus";
import Reactions from "./reactions.vue";

export default {
  props: [
    "bulk-selectable",
    "bulk-selected",
    "original-upvotes",
    "current-user",
    "original-post",
    "current-project-admin",
    "show-board",
    "allow-anonymous",
    "allow-vote-count",
    "status-name",
    "is-board",
  ],
  data: function () {
    return {
      post: this.originalPost,
      upvotes: this.originalPost.total_upvotes,
      lines: 5,
      upvoteIcon,
      tagsIcon,
      commentsIcon,
      eyeIcon,
      eyeSlashIcon,
      warningIcon,
      isVoting: false,
      isAnimating: false,
    };
  },
  components: {
    upvoteIcon,
    tagsIcon,
    commentsIcon,
    eyeIcon,
    eyeSlashIcon,
    warningIcon,
    Reactions,
  },
  methods: {
    commentsCountHuman: function () {
      var message = "";

      if (this.post.comments_count === 0) {
        message = "0";
      } else if (this.post.comments_count === 1) {
        message = "1";
      } else {
        message = this.post.comments_count;
      }

      return message;
    },
    stripHtml(html) {
      if (!html) return '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    },
    content() {
      var text = this.stripHtml(this.post.content);
      text = text.replace(/[\u0022\u201C\u201D]/g, "");
      if (text.length < 2) {
        text = null;
      }
      return text;
    },
    authorName() {
      if (this.post.author_name) return this.post.author_name;
      if (this.post.user && this.post.user.name) return this.post.user.name;
      return "Anonymous";
    },
    authorAvatarUrl() {
      if (this.post.author_avatar_url) return this.post.author_avatar_url;
      if (this.post.user && this.post.user.avatar_url) return this.post.user.avatar_url;
      return null;
    },
    castVote: async function () {
      if (this.isVoting || this.votesDisabled) return;

      // Handle unauthorized users
      if (!this.allowAnonymous && !this.currentUser) {
        EventBus.$emit("openRegModalSignup");
        return;
      }

      // Check for anonymous user cooldown
      if (!this.currentUser) {
        const lastVoteTime = localStorage.getItem('lastVoteTime_' + this.post.id);
        const currentTime = new Date().getTime();
        if (lastVoteTime && currentTime - lastVoteTime < 1000) {
          return;
        }
      }

      this.isVoting = true;

      // Add animation logic
      if (!this.alreadyVoted) {
        this.isAnimating = true;
        setTimeout(() => {
          this.isAnimating = false;
        }, 300);
      }

      // Optimistically update UI
      const previousVoteState = this.post.current_user_voted_for;
      const voteChange = previousVoteState ? -1 : 1;
      this.post.current_user_voted_for = !previousVoteState;
      this.upvotes += voteChange;

      try {
        const vote_post_url = "/boards/" + this.post.board_id + "/posts/" + this.post.id + "/vote";
        const data = new FormData();

        const response = await new Promise((resolve, reject) => {
          Rails.ajax({
            url: vote_post_url,
            type: "POST",
            data: data,
            dataType: "json",
            success: resolve,
            error: reject,
          });
        });

        if (response.status === "unprocessable_entity") {
          // Revert optimistic updates on failure
          this.post.current_user_voted_for = previousVoteState;
          this.upvotes -= voteChange;
          return;
        }

        if (typeof response.votes !== 'undefined') {
          EventBus.$emit("postVotes", {
            id: this.post.id,
            total: response.votes,
          });
          this.upvotes = response.votes;

          if (!this.currentUser) {
            localStorage.setItem('lastVoteTime_' + this.post.id, new Date().getTime());
          }
        }
      } catch (error) {
        // Revert optimistic updates on error
        this.post.current_user_voted_for = previousVoteState;
        this.upvotes -= voteChange;
        console.error('Error voting:', error);
      } finally {
        this.isVoting = false;
      }
    },
    handleRowClick(event) {
      // In select mode the whole row toggles selection instead of navigating.
      if (this.bulkSelectable) {
        this.$emit("toggle-bulk", this.post.id, event && event.shiftKey);
        return;
      }
      this.toIdeaPage();
    },

    toIdeaPage() {
      window.location.href = this.post.url;
    },
  },

  computed: {
    postTags: function () {
      return this.post.tags.map((tag) => tag.name).join(", ");
    },
    alreadyVoted() {
      return this.post.current_user_voted_for;
    },
    votesDisabled() {
      return !!(this.post.status && this.post.status.disable_votes);
    },
  },
};
</script>

<style scoped>
.vote-closed {
  cursor: default;
}

.vote-closed svg {
  opacity: 0.35;
}
</style>
